import { ArcSeg2d } from '../math/arcSeg2d';
import { IPoint2d } from '../math/IPoint2d';
import { LineSeg2d } from '../math/LineSeg2d';
import { Point2dUtils } from '../math/Point2dUtils';
import { pointEqual } from '../math/utils';
import { Edge } from '../model/polygon/Region';
import { EFaceSide, IEdgeWallSideInfoWithRelative, IWallEdge } from '../types';
import { IShapePoint2d } from '../types/shape';
import { isArc } from './arc';

interface ICollinearEdgeInfo {
  start: IPoint2d;
  end: IPoint2d;
  center?: IPoint2d;
  clockwise?: boolean;
  side?: EFaceSide;
  sepSide?: EFaceSide;
  count: number;
  wallId?: string;
  sepId?: string;
}

export function edgeToWallEdge(edge: Edge<IEdgeWallSideInfoWithRelative>): IWallEdge {
  const obj: IWallEdge = {
    start: edge.from,
    end: edge.to,
    wallId: edge.info.wallId,
    side: edge.info.side,
    startId: edge.info.startId,
    endId: edge.info.endId
  };
  if (edge.info.sepId) {
    obj.sepId = edge.info.sepId;
    obj.sepSide = edge.info.sepSide;
    obj.sepType = edge.info.sepType;
    obj.regionId = edge.info.regionId;
  }
  if (edge.center) {
    obj.center = edge.center;
    obj.clockwise = edge.clockwise;
  }
  return obj;
}

/** 端点有 `from`/`to` 与 `start`/`end` 两套字段名，二者必有其一 */
type TPathEdge =
  | { from: IPoint2d; to: IPoint2d; start?: IPoint2d; end?: IPoint2d; center?: IPoint2d; clockwise?: boolean }
  | { from?: IPoint2d; to?: IPoint2d; start: IPoint2d; end: IPoint2d; center?: IPoint2d; clockwise?: boolean };

/** 端点以 `from`/`to` 命名的那一支。收窄后两者均为必选，另一支则必带 `start`/`end` */
function isFromToEdge(edge: TPathEdge): edge is TPathEdge & { from: IPoint2d; to: IPoint2d } {
  return edge.from !== undefined && edge.to !== undefined;
}

export function getPathFromEdges(_edges: TPathEdge[]): IShapePoint2d[] {
  if (_edges.length === 0) return [];

  const edges = _edges.map((edge) =>
    isFromToEdge(edge)
      ? { start: edge.from, end: edge.to, center: edge.center, clockwise: edge.clockwise }
      : { start: edge.start, end: edge.end, center: edge.center, clockwise: edge.clockwise }
  );

  const path = edges.map((edge) => {
    if (isArc(edge)) {
      const arc = new ArcSeg2d({ start: edge.start, end: edge.end, center: edge.center, clockwise: edge.clockwise });
      return {
        x: edge.start.x,
        y: edge.start.y,
        // sagitta: arc.getSagitta()
        center: arc.center,
        clockwise: arc.clockwise
      };
    }

    return { x: edge.start.x, y: edge.start.y };
  });

  const startPoint = edges[0].start;
  const endPoint = edges[edges.length - 1].end;
  // 判断是否为环
  if (Point2dUtils.equals(startPoint, endPoint)) return path;

  // 非环，则添加终点
  path.push(endPoint);
  return path;
}

// edges需要首位相连
export function mergeCollinearLoopEdges(
  edges: Omit<ICollinearEdgeInfo, 'count'>[],
  onlyMergeSameWall: boolean = false
): Omit<ICollinearEdgeInfo, 'count'>[] {
  edges = edges.filter((e) => !e.center); // 过滤掉弧形
  if (edges.length < 3) return edges;

  const result = [];
  const legalEdge = (edge: ICollinearEdgeInfo) =>
    edge.count > 1 ||
    edge.side === EFaceSide.MainLeftSide ||
    edge.side === EFaceSide.MainRightSide ||
    (edge.side === EFaceSide.Empty && edge.sepSide === EFaceSide.Single);

  // ========= 先处理下起始点，如果和尾部线段平行，进行合并
  let startIndex = 0;
  let endIndex = edges.length - 1;
  let mergedEdge: ICollinearEdgeInfo | null = { ...edges[0], count: 1 };
  startIndex += 1;
  while (startIndex < endIndex) {
    const startEdge = edges[startIndex];
    const endEdge = edges[endIndex];
    const sameWallCheck = onlyMergeSameWall
      ? mergedEdge.wallId === endEdge.wallId && mergedEdge.sepId === endEdge.sepId
      : true;

    if (sameWallCheck && pointEqual(mergedEdge.start, endEdge.end) && sameDirection(mergedEdge, endEdge)) {
      mergedEdge.start = endEdge.start;
      mergedEdge.count++;
      endIndex--;
    } else if (sameWallCheck && pointEqual(mergedEdge.end, startEdge.start) && sameDirection(mergedEdge, startEdge)) {
      mergedEdge.end = startEdge.end;
      mergedEdge.count++;
      startIndex++;
    } else {
      break;
    }
  }
  if (legalEdge(mergedEdge)) {
    result.push({ ...mergedEdge });
  }

  // ========= 开始从startIndex开始进行处理
  mergedEdge = null;
  for (let i = startIndex; i <= endIndex; i++) {
    const edgeInfo = edges[i];
    const currentEdge = {
      start: edgeInfo.start,
      end: edgeInfo.end,
      side: edgeInfo.side,
      sepSide: edgeInfo.sepSide,
      wallId: edgeInfo.wallId,
      sepId: edgeInfo.sepId,
      count: 1
    };

    if (!mergedEdge) {
      mergedEdge = currentEdge;
      continue;
    }

    const sameWallCheck = onlyMergeSameWall
      ? mergedEdge.wallId === currentEdge.wallId && mergedEdge.sepId === currentEdge.sepId
      : true;
    if (sameWallCheck && pointEqual(mergedEdge.end, currentEdge.start) && sameDirection(mergedEdge, currentEdge)) {
      mergedEdge = {
        start: mergedEdge.start,
        end: currentEdge.end,
        side: currentEdge.side,
        sepSide: currentEdge.sepSide,
        wallId: currentEdge.wallId,
        sepId: currentEdge.sepId,
        count: mergedEdge.count + 1
      };
      continue;
    }

    if (legalEdge(mergedEdge)) {
      result.push({ ...mergedEdge });
    }
    mergedEdge = currentEdge;
  }

  if (mergedEdge && legalEdge(mergedEdge)) {
    result.push({ ...mergedEdge });
  }

  return result;
}

function sameDirection(
  edge1: Pick<ICollinearEdgeInfo, 'start' | 'end'>,
  edge2: Pick<ICollinearEdgeInfo, 'start' | 'end'>
) {
  return new LineSeg2d(edge1.start, edge1.end).isSegmentsSameDirection(new LineSeg2d(edge2.start, edge2.end));
}
